import { type UserType, useListingRequestFinancingMutation } from '@kijiji/generated/graphql-types'
import { type FormikProps, Formik } from 'formik'
import { useSession } from 'next-auth/react'
import { Trans, useTranslation } from 'next-i18next'
import { type FC, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import {
  type TradeInFormFields,
  FORM_FIELDS,
} from '@/components/vip/vip-trade-in/constants/vipTradeIn'
import { buildTradeInMessage } from '@/components/vip/vip-trade-in/utils/buildTradeInMessage'
import { getDefaultFormValues } from '@/components/vip/vip-trade-in/utils/getDefaultFormValues'
import { getTradeInValidationObject } from '@/components/vip/vip-trade-in/utils/getTradeInValidationObject'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { isNpvDealer } from '@/features/profile/utils/isNpvDealer'
import { useFormValidation } from '@/hooks/useFormValidation'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
import { Modal } from '@/ui/molecules/modal/Modal'
import { SystemMessage } from '@/ui/molecules/system-message'
import { TextField } from '@/ui/molecules/text-field'
import { sendToLogger } from '@/utils/sendToLogger'

export type VipTradeInModalProps = {
  externalSourceId: string
  listingId: string
  userType: UserType
  listingUrl: string
}

export const VipTradeInModal: FC<VipTradeInModalProps> = ({
  externalSourceId,
  listingId,
  listingUrl,
  userType,
}) => {
  const { isOpen, handleCloseModal: closeTradeInModal } = useModal(MODAL_REGISTRY.TRADE_IN)

  const { status, data } = useSession()
  const userData = data?.user

  const { t } = useTranslation([TRANSLATION_KEYS.TRADE_IN, TRANSLATION_KEYS.COMMON])
  const formRef = useRef<FormikProps<TradeInFormFields>>(null)
  const { validateForm } = useFormValidation()
  const { spacing } = useTheme()

  const [completedInquiry, setCompletedInquiry] = useState<boolean>(false)
  const [requestFinancing, { loading, error: requestError }] = useListingRequestFinancingMutation()

  if (!isOpen || !isUserAuthenticated(status)) return null

  const defaultFormValues = getDefaultFormValues(userData?.email, userType)

  const handleValidation = (values: TradeInFormFields) => {
    const validationObj = getTradeInValidationObject(userType)

    const errors = validateForm(validationObj, values)

    return errors
  }

  const handleRequestSubmit = async () => {
    const formValues = formRef.current?.values

    if (!formValues) return

    const message = buildTradeInMessage({
      formValues: formRef.current?.values,
      listingId,
      listingUrl,
      t,
    })

    try {
      await requestFinancing({
        variables: {
          email: formValues[FORM_FIELDS.EMAIL],
          externalSourceId,
          firstName: formValues[FORM_FIELDS.FIRST_NAME],
          lastName: formValues[FORM_FIELDS.LAST_NAME],
          listingRequestFinancingId: listingId,
          message: message,
          phoneNumber: formValues[FORM_FIELDS.PHONE],
        },
      })
      trackEvent({ action: GA_EVENT.AutosCtaClick, label: `cta=tradein` })
      setCompletedInquiry(true)
    } catch (err) {
      sendToLogger(err, { fingerprint: ['tradeInEstimate'] })
    }
  }

  const handleCloseModal = () => {
    setCompletedInquiry(false)
    closeTradeInModal()
  }

  return (
    <Modal
      closeButtonLabel={t('common:modals.close.button.label')}
      hasCloseButton
      id={MODAL_REGISTRY.TRADE_IN}
      isOpen={isOpen}
      label={t('trade_in:modal.title')}
      onCancel={handleCloseModal}
      width={{ small: '100vw', medium: '60rem' }}
    >
      <Spacing pTop={spacing.large} pBottom={spacing.large}>
        <Flex flexDirection="column" gap={spacing.default}>
          <HeadlineText as="h3" size="large" textAlign="center">
            {t('trade_in:modal.title')}
          </HeadlineText>

          <BodyText textAlign="center" size="medium">
            {t('vip:dealer_request.modal.description')}
          </BodyText>

          {requestError ? (
            <SystemMessage
              description={t('common:forms.errors.generic.description')}
              hideCloseButton
              title={t('common:forms.errors.generic.title')}
              variation="error"
            />
          ) : null}
        </Flex>
      </Spacing>

      {completedInquiry ? (
        <SystemMessage
          variation="success"
          hideCloseButton
          title={t('vip:dealer_request.success_message.title')}
          description={t('vip:dealer_request.success_message.description')}
        />
      ) : (
        <Formik
          initialValues={defaultFormValues}
          innerRef={formRef}
          onSubmit={handleRequestSubmit}
          validate={handleValidation}
          validateOnBlur={true}
          validateOnChange={false}
        >
          {({ handleChange, values, errors, handleSubmit }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Flex flexDirection="column" gap={spacing.large}>
                  <Flex flexDirection="column" gap={spacing.default}>
                    <HeadlineText as="h4" size="medium">
                      {t('trade_in:modal.subtitle.vehicle_details')}
                    </HeadlineText>

                    {isNpvDealer(userType) && (
                      <TextField
                        bottom="0"
                        error={
                          errors[FORM_FIELDS.VEHICLE_TYPE] &&
                          t(`common:${errors[FORM_FIELDS.VEHICLE_TYPE]}`)
                        }
                        id={FORM_FIELDS.VEHICLE_TYPE}
                        label={t('trade_in:vehicle.type.label')}
                        onChange={handleChange}
                        value={values[FORM_FIELDS.VEHICLE_TYPE]}
                        helperText={t('trade_in:vehicle.type.vehicleTypeHelperText')}
                      />
                    )}

                    <TextField
                      bottom="0"
                      error={errors[FORM_FIELDS.YEAR] && t(`common:${errors[FORM_FIELDS.YEAR]}`)}
                      id={FORM_FIELDS.YEAR}
                      label={t('trade_in:vehicle.year.label')}
                      onChange={handleChange}
                      value={values[FORM_FIELDS.YEAR]}
                    />

                    <TextField
                      bottom="0"
                      error={errors[FORM_FIELDS.MAKE] && t(`common:${errors[FORM_FIELDS.MAKE]}`)}
                      id={FORM_FIELDS.MAKE}
                      label={t('trade_in:vehicle.make.label')}
                      onChange={handleChange}
                      value={values[FORM_FIELDS.MAKE]}
                    />

                    {!isNpvDealer(userType) && (
                      <TextField
                        bottom="0"
                        error={
                          errors[FORM_FIELDS.MODEL] && t(`common:${errors[FORM_FIELDS.MODEL]}`)
                        }
                        id={FORM_FIELDS.MODEL}
                        label={t('trade_in:vehicle.model.label')}
                        onChange={handleChange}
                        value={values[FORM_FIELDS.MODEL]}
                      />
                    )}

                    <TextField
                      bottom="0"
                      endAdornment="km"
                      error={
                        errors[FORM_FIELDS.MILEAGE] && t(`common:${errors[FORM_FIELDS.MILEAGE]}`)
                      }
                      id={FORM_FIELDS.MILEAGE}
                      label={t('trade_in:vehicle.mileage.label')}
                      onChange={handleChange}
                      type="number"
                      value={values[FORM_FIELDS.MILEAGE]}
                    />
                  </Flex>

                  <Flex flexDirection="column" gap={spacing.default}>
                    <HeadlineText as="h4" size="medium">
                      {t('trade_in:modal.subtitle.contact_info')}
                    </HeadlineText>

                    <TextField
                      bottom="0"
                      error={
                        errors[FORM_FIELDS.FIRST_NAME] &&
                        t(`common:${errors[FORM_FIELDS.FIRST_NAME]}`)
                      }
                      id={FORM_FIELDS.FIRST_NAME}
                      label={t('common:forms.inputs.first_name.label')}
                      onChange={handleChange}
                      autoComplete="given-name"
                      value={values[FORM_FIELDS.FIRST_NAME]}
                    />

                    <TextField
                      bottom="0"
                      error={
                        errors[FORM_FIELDS.LAST_NAME] &&
                        t(`common:${errors[FORM_FIELDS.LAST_NAME]}`)
                      }
                      id={FORM_FIELDS.LAST_NAME}
                      label={t('common:forms.inputs.last_name.label')}
                      onChange={handleChange}
                      autoComplete="family-name"
                      value={values[FORM_FIELDS.LAST_NAME]}
                    />

                    <TextField
                      bottom="0"
                      error={errors[FORM_FIELDS.EMAIL] && t(`common:${errors[FORM_FIELDS.EMAIL]}`)}
                      id={FORM_FIELDS.EMAIL}
                      label={t('common:forms.inputs.email.label')}
                      onChange={handleChange}
                      autoComplete="email"
                      value={values[FORM_FIELDS.EMAIL]}
                    />

                    <TextField
                      bottom="0"
                      error={errors[FORM_FIELDS.PHONE] && t(`common:${errors[FORM_FIELDS.PHONE]}`)}
                      id={FORM_FIELDS.PHONE}
                      label={t('common:forms.inputs.phone.label')}
                      onChange={handleChange}
                      autoComplete="tel"
                      value={values[FORM_FIELDS.PHONE]}
                    />
                  </Flex>

                  <Button isFullWidth type="submit" isLoading={loading} disabled={loading}>
                    {t('vip:dealer_request.inquiry_label')}
                  </Button>
                </Flex>
              </form>
            )
          }}
        </Formik>
      )}

      <Spacing mTop={spacing.default}>
        <BodyText size="xSmall">
          <Trans
            i18nKey="trade_in:disclaimer"
            components={[
              <LinkCustom
                size="xSmall"
                key="privacyCheckboxLink"
                variant="secondary"
                href={`${t('routes:external.terms_of_use.href')}`}
                openInNewTab={true}
                noChildren={true}
                hasUnderline
              />,
              <LinkCustom
                size="xSmall"
                key="privacyCheckboxLink"
                variant="secondary"
                href={`${t('routes:external.privacy_policy.href')}`}
                openInNewTab={true}
                noChildren={true}
                hasUnderline
              />,
            ]}
          />
        </BodyText>
      </Spacing>
    </Modal>
  )
}
